import React from 'react'
import { useSelector } from 'react-redux'
import { DataGrid } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import Constants from '../../../../../constants/en.json'
import { IconButton } from '../../../../../components/mui/Core/index'
import { DeleteForeverIcon, EditIcon } from '../../../../../components/mui/Icons'
import { useApplicationList } from '../../../../../hooks/applications/application-list'
import { getDateTimeFormattedValue } from '../../../../../utils/string-utils'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'

function PermissionManagementTable({
  filterBy,
  filterValue,
  searchByOptions,
  handleDeletePermissionModalOpen,
}) {
  const navigate = useNavigate()
  const applicationList = useApplicationList()
  const isFetchingAllPermissions = useSelector(
    (state) => state?.permissionManagement?.viewPermission?.isFetchingAllPermissions,
  )
  const allPermissions = useSelector(
    (state) => state?.permissionManagement?.viewPermission?.allPermissions,
  )
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''

  const rows = allPermissions?.map((row) => ({
    id: row.name,
    code: row.name,
    description: row.description,
    application: row.appName,
    dateCreated: dayjs(row.createdAt)?.valueOf(),
    dateModified: dayjs(row.lastModified)?.valueOf(),
    modifiedBy: row.modifiedBy,
  }))

  const handleEditPermission = (row) => () => {
    const selectedPermission = { ...row }
    navigate('/role-management/manage-permissions/edit-permission', {
      state: {
        isEditingPermission: true,
        selectedPermission,
      },
    })
  }
  const handleDeletePermission = (row) => () => {
    handleDeletePermissionModalOpen(row)
  }

  const columns = [
    {
      field: 'code',
      headerName: Constants.roleManagement.permissionManagement.PERMISSION_CODE,
      type: 'string',
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'description',
      headerName: Constants.roleManagement.permissionManagement.PERMISSION_DESCRIPTION,
      type: 'string',
      minWidth: 190,
      flex: 1,
    },
    {
      field: 'application',
      headerName: Constants.roleManagement.permissionManagement.APPLICATION,
      type: 'string',
      minWidth: 150,
      flex: 1,
      valueGetter: (value) => applicationList[value],
    },
    {
      field: 'dateCreated',
      headerName: Constants.roleManagement.permissionManagement.DATE_CREATED,
      type: 'string',
      minWidth: 120,
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => getDateTimeFormattedValue(value),
    },
    {
      field: 'dateModified',
      headerName: Constants.roleManagement.permissionManagement.DATE_MODIFIED,
      type: 'string',
      minWidth: 120,
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => getDateTimeFormattedValue(value),
    },
    {
      field: 'modifiedBy',
      headerName: Constants.roleManagement.permissionManagement.MODIFIED_BY,
      type: 'string',
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'Actions',
      headerName: Constants.roleManagement.ACTIONS,
      type: 'string',
      minWidth: 120,
      flex: 0.5,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <>
            <IconButton onClick={handleEditPermission(cellValues?.row)}>
              <EditIcon color='action' />
            </IconButton>
            <IconButton onClick={handleDeletePermission(cellValues?.row)}>
              <DeleteForeverIcon color='error' />
            </IconButton>
          </>
        )
      },
    },
  ]

  const filterModel = {
    items: [{ field: filterBy, operator: 'contains', value: filterValue }],
  }

  const gridStyle = {
    minHeight: '160px',
    height: isFetchingAllPermissions ? '400px' : 'auto',
  }

  return (
    <DataGrid
      sx={gridStyle}
      rows={rows}
      columns={columns}
      filterModel={filterModel}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 25, page: 0 },
        },
        sorting: {
          sortModel: [{ field: 'dateModified', sort: 'desc' }],
        },
      }}
      loading={isFetchingAllPermissions}
      pageSizeOptions={[25, 50]}
      spacing={1}
      sortingOrder={['asc', 'desc']}
      disableColumnFilter
      disableRowSelectionOnClick
      slotProps={{
        pagination: {
          component: 'div',
          showLastButton: true,
          showFirstButton: true,
        },
        noResultsOverlay: {
          filterByDescription,
          noResultsValue: filterValue,
        },
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
      slots={{
        noResultsOverlay: NoResultsFoundTableOverlay,
      }}
    />
  )
}

PermissionManagementTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  handleDeletePermissionModalOpen: PropTypes.func.isRequired,
}

export default PermissionManagementTable
